import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faChevronDown,
	faListCheck,
	faPlus,
	faUser,
	faXmark,
	faMessage,
	faWandMagicSparkles,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { FewShotExample, MAX_FEW_SHOT_EXAMPLES } from "../schema/aiSnippet";

interface FewShotExamplesProps {
	examples: FewShotExample[];
	canPrefillFromLeads: boolean;
	onChange: (examples: FewShotExample[]) => void;
	onPrefillFromLeads: () => void;
}

export const FewShotExamples: FunctionComponent<FewShotExamplesProps> = ({
	examples,
	canPrefillFromLeads,
	onChange,
	onPrefillFromLeads,
}) => {
	const { t } = useTranslation();
	const [collapsed, setCollapsed] = useState<boolean>(false);

	const canAdd = examples.length < MAX_FEW_SHOT_EXAMPLES;

	const handleAdd = () => {
		if (!canAdd) {
			return;
		}

		onChange([...examples, { leadContext: "", idealOutput: "" }]);
	};

	const handleRemove = (index: number) => {
		onChange(examples.filter((_, i) => i !== index));
	};

	const handleUpdate = (index: number, patch: Partial<FewShotExample>) => {
		onChange(examples.map((example, i) => (i === index ? { ...example, ...patch } : example)));
	};

	return (
		<div className="bg-white border border-border-light rounded-xl p-3.5 mb-3">
			<button
				aria-expanded={!collapsed}
				className="w-full flex items-center gap-1.5 bg-transparent border-0 p-0 cursor-pointer text-left font-semibold text-text text-sm"
				type="button"
				onClick={() => setCollapsed((c) => !c)}
			>
				<Icon className="w-3 h-3 text-text-meta" icon={faListCheck} />
				<span>{t("aiSnippet.modal.fewShot.title")}</span>
				<span className="text-[10px] font-medium text-text-meta bg-background-light px-1.5 py-0.5 rounded-md">
					{t("aiSnippet.modal.fewShot.optional")}
				</span>
				<span className="ml-1 text-xs font-medium text-text-meta">
					{t("aiSnippet.modal.fewShot.counter", {
						count: examples.length,
						max: MAX_FEW_SHOT_EXAMPLES,
					})}
				</span>
				<Icon
					className={classNames(
						"ml-auto w-3.5 h-3.5 text-text-meta transition-transform",
						collapsed && "-rotate-90"
					)}
					icon={faChevronDown}
				/>
			</button>

			{!collapsed && (
				<div className="mt-2.5">
					<div className="text-xs text-text-meta mb-3 leading-relaxed">
						{t("aiSnippet.modal.fewShot.subtitle")}
					</div>

					{canPrefillFromLeads && (
						<button
							className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-action-200 border border-blue-action/30 rounded-md text-blue-action text-xs font-medium cursor-pointer hover:bg-blue-action hover:text-white transition-colors mb-2"
							type="button"
							onClick={onPrefillFromLeads}
						>
							<Icon className="w-3 h-3" icon={faWandMagicSparkles} />
							{t("aiSnippet.modal.fewShot.prefillFromLeads")}
						</button>
					)}

					<div>
						{examples.map((example, index) => (
							<div
								key={index}
								className="border border-border-light rounded-lg p-2.5 bg-white mb-2 relative"
							>
								<div className="flex items-center justify-between mb-1.5">
									<span className="text-[11px] text-text-meta font-semibold uppercase tracking-wider">
										{t("aiSnippet.modal.fewShot.exampleNum", { index: index + 1 })}
									</span>
									<button
										aria-label={t("aiSnippet.modal.fewShot.removeExample")}
										className="w-5 h-5 border-0 bg-transparent rounded cursor-pointer text-text-meta hover:bg-status-error-light hover:text-status-error flex items-center justify-center"
										type="button"
										onClick={() => handleRemove(index)}
									>
										<Icon className="w-3 h-3" icon={faXmark} />
									</button>
								</div>

								<div className="mb-2">
									<div className="text-[10px] font-semibold text-text-meta uppercase tracking-wider mb-1 flex items-center gap-1">
										<Icon className="w-2.5 h-2.5" icon={faUser} />
										{t("aiSnippet.modal.fewShot.leadContextLabel")}
									</div>
									<textarea
										className="w-full px-2 py-1.5 border border-border-light rounded-md text-xs bg-white leading-snug resize-y min-h-[48px] focus:outline-none focus:border-blue-action focus:shadow-[0_0_0_2px_rgba(29,61,242,0.08)]"
										placeholder={t("aiSnippet.modal.fewShot.leadContextPlaceholder")}
										value={example.leadContext}
										onChange={(e) => handleUpdate(index, { leadContext: e.target.value })}
									/>
								</div>

								<div>
									<div className="text-[10px] font-semibold text-text-meta uppercase tracking-wider mb-1 flex items-center gap-1">
										<Icon className="w-2.5 h-2.5" icon={faMessage} />
										{t("aiSnippet.modal.fewShot.idealOutputLabel")}
									</div>
									<textarea
										className="w-full px-2 py-1.5 border border-border-light rounded-md text-xs bg-white leading-snug resize-y min-h-[48px] focus:outline-none focus:border-blue-action focus:shadow-[0_0_0_2px_rgba(29,61,242,0.08)]"
										placeholder={t("aiSnippet.modal.fewShot.idealOutputPlaceholder")}
										value={example.idealOutput}
										onChange={(e) => handleUpdate(index, { idealOutput: e.target.value })}
									/>
								</div>
							</div>
						))}
					</div>

					<button
						className={classNames(
							"w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-transparent border border-dashed border-gray-300 rounded-lg text-text-meta text-xs font-medium transition-all",
							canAdd
								? "cursor-pointer hover:border-blue-action hover:text-blue-action hover:bg-blue-action-200 hover:border-solid"
								: "opacity-40 cursor-not-allowed"
						)}
						disabled={!canAdd}
						type="button"
						onClick={handleAdd}
					>
						<Icon className="w-3 h-3" icon={faPlus} />
						{t("aiSnippet.modal.fewShot.addExample")}
					</button>
				</div>
			)}
		</div>
	);
};
